import axios from "axios";
import { safeOnboardingNext } from "./onboardingNext";

// Use current origin for API calls so it works on any domain (preview or custom)
const API_URL = window.location.origin;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  // 25 s timeout — prevents the entire UI from hanging on a single slow
  // request (e.g. Atlas latency spike). Without this, axios default is
  // INFINITE, which causes the "spinner forever" symptom in Production.
  timeout: 25000,
});

// Tracks the last successful API call so the idle-timeout warning (see
// AuthContext.js) can predict the server-side kick, which only resets on
// real API activity — not on mouse movement alone.
//
// Hallazgo de cx-revisor (2026-10-15): el backend EXCLUYE explícitamente
// /auth/logout, /auth/refresh, /auth/me, /auth/2fa, /audit/sessions y
// /audit/log de su propio chequeo de inactividad (server.py, auth_skip).
// Si contáramos esas llamadas aquí, el aviso subestimaría la inactividad
// real del servidor (p. ej. el refresh silencioso cada 30 min lo
// "resetearía" sin que el servidor lo haya contado) y casi nunca
// aparecería. Debe espejar exactamente esa misma lista.
const IDLE_WARNING_EXCLUDED_PATHS = ["/auth/logout", "/auth/refresh", "/auth/me", "/auth/2fa", "/audit/sessions", "/audit/log"];
export let lastApiActivityAt = Date.now();

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => {
    const url = response.config?.url || "";
    if (!IDLE_WARNING_EXCLUDED_PATHS.some((p) => url.includes(p))) {
      lastApiActivityAt = Date.now();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const detail = error.response?.data?.detail;
    const isLicenseExpired = detail && typeof detail === "object" && detail.code === "LICENSE_EXPIRED";
    const isPasswordChangeRequired = detail && typeof detail === "object" && detail.code === "PASSWORD_CHANGE_REQUIRED";

    // Forced initial-password change: the account is still valid/logged in
    // (unlike license-expired) — just redirect to the dedicated screen
    // instead of logging out. The backend blocks every other endpoint until
    // this completes, so bounce there on the FIRST such response.
    if (isPasswordChangeRequired) {
      if (window.location.pathname !== "/force-password-change") {
        window.location.href = "/force-password-change";
      }
      return Promise.reject(error);
    }

    // License-expired: short-circuit the refresh dance and force-logout
    // with a flag the LoginPage can read to show the blocked message.
    if (isLicenseExpired) {
      try {
        sessionStorage.setItem("license_expired_msg", detail.msg || "Cuenta bloqueada por falta de pago.");
      } catch { /* sessionStorage may be unavailable */ }
      // Best-effort clear of the access cookie so axios doesn't loop
      try { await api.post("/auth/logout"); } catch { /* ignore */ }
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes("/auth/")) {
      originalRequest._retry = true;
      try {
        await api.post("/auth/refresh");
        return api(originalRequest);
      } catch {
        // N1 (validador único de next, ver lib/onboardingNext.js): si la
        // sesión vence a mitad del wizard de onboarding Beauty
        // (/onboarding/:token), preservamos esa ruta en `next` para que
        // LoginPage pueda devolver al cliente al MISMO enlace tras volver
        // a loguearse, en vez de soltarlo siempre en /login a secas.
        // Cualquier otra ruta (p. ej. /dashboard) sigue yendo a /login
        // sin next -- safeOnboardingNext es el único criterio de qué
        // ruta es "segura" para esto.
        const next = safeOnboardingNext(window.location.pathname);
        window.location.href = next ? `/login?next=${encodeURIComponent(next)}` : "/login";
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Algo salió mal. Intente de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
